import { type SourceLocation } from "graphql";
import { onError } from '@apollo/client/link/error';
import { isServer } from '../executionEnvironment';
import { graphqlBatchMaxSetting } from '../instanceSettings';
import { ApolloLink, Operation, selectURI } from "@apollo/client/core";
import { crosspostUserAgent } from "./constants";
import { getSiteUrl } from "../vulcan-lib/utils";
import { StreamingGraphqlHttpLink } from "./StreamingGraphqlHttpLink";

/**
 * "Links" are Apollo's way of defining the source to read our data from, and they need to
 * be set up differently depending on whether we're rendering on the server or on the client,
 * and whether we're rendering local data or foreign crosspost data. Multiple links can be
 * chained together, for instance, for error handling.
 *
 * https://www.apollographql.com/docs/react/api/link/introduction/
 */

/**
 * Http link is used for client side rendering
 */
export const createHttpLink = (baseUrl: string, loginToken: string|null) => {
  const uri = baseUrl + "api/streamGraphql";

  const batchKey = (operation: Operation) => {
    // The default part is copied from https://github.com/apollographql/apollo-client/blob/main/src/link/batch-http/batchHttpLink.ts#L192-L206
    const context = operation.getContext();

    const contextConfig = {
      http: context.http,
      options: context.fetchOptions,
      credentials: context.credentials,
    };

    const defaultBatchKey = selectURI(operation, uri) + JSON.stringify(contextConfig);

    // If the operation has a batchKey variable, add that to the batch key.
    // This is to manually separate out very slow queries
    const explicitBatchKey = context.batchKey;

    return explicitBatchKey && typeof explicitBatchKey === "string" ? defaultBatchKey : defaultBatchKey + explicitBatchKey;
  };

  const isSameSiteRequest = baseUrl === '/' || baseUrl === getSiteUrl();

  const fetch: typeof globalThis.fetch = isServer
    ? (url, options) => globalThis.fetch(url, {
      ...options,
      headers: {
        ...options?.headers,
        // user agent because LW bans bot agents
        'User-Agent': crosspostUserAgent,
        ...(loginToken ? { loginToken } : {}),
      },
    })
    : globalThis.fetch;
  return new StreamingGraphqlHttpLink({
    uri,
    credentials: isSameSiteRequest ? 'same-origin' : 'omit',
    batchMax: isServer ? 1 : graphqlBatchMaxSetting.get(),
    headers: {
      'accept': 'application/json',
    },
    fetch,
    batchKey,
  });
}

export const headerLink = new ApolloLink((operation, forward) => {
  if (!isServer) {
    const url = new URL(window.location.href)
    const path = url.pathname + url.search

    const headers = {
      'request-origin-path': path,
      'x-apollo-operation-name': operation.operationName,
    };

    operation.setContext({
      headers,
    });
  }
  return forward(operation);
});

const locationsToStr = (locations: readonly SourceLocation[] = []) =>
  locations.map(({column, line}) => `line ${line}, col ${column}`).join(';');

// TODO: decide whether we want to filter out noisy logs like various `app.` errors (permissions and missing documents)
/**
 * This is an extra utility link that is currently used for client side error handling
 */
export const createErrorLink = () =>
  onError((errorResponse) => {
    const { error } = errorResponse;
    // eslint-disable-next-line no-console
    // console.error(error.message);
  });
